import { apiFetch, serviceBase } from './http';

/* ОДИН ПЛАН УХОДА — `post-care-api`, пациентская половина.

       GET  {gw}/post-care-api.application/api/v1/read/patient/post-care/channel/plan?channelId=
       POST {gw}/post-care-api.application/api/v1/write/patient/post-care/check?checkId=

   Скоуп обеих ручек — АККАУНТ звонящего, и никакого `patientId` в подписи нет:
   назвать чужую карту пациент не вправе, свою — не должен. Чужой план отвечает
   404 тем же телом, что несуществующий, — по ответу их не различить, и это
   намеренно.

   ОТМЕТКА ПУНКТА — ЕДИНСТВЕННЫЙ ПИСАТЕЛЬ `done` во всём флоте. До появления
   этого экрана поле приезжало вечным `false`, и прогресс плана был мёртвым
   числом: врач видел «0 из 9» у человека, который всё сделал.

   ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ — не «пока не подключено», а не существует:

     • ЗАГРУЗКИ СНИМКОВ — и это перестало быть правдой. Ручки приёма файлов у
       постухода по-прежнему нет ни одной и не будет: файлы живут в
       `document-api`, и кабинет ходит туда САМ (`data/photos.ts`). Требование
       врача («на этом этапе 2 фото») по-прежнему приезжает полем `need` этапа —
       оно и остаётся единственным, что постуход знает о снимках;
     • ПЕРЕПИСКИ И ЗВОНКА ВРАЧУ. Канала связи во флоте нет ни одного, телефона
       врача нет и в ответе: `doctor` несёт имя, инициалы и клинику;
     • ЗАПИСИ НА ПОВТОРНЫЙ ВИЗИТ. Контрольный визит — это ЭТАП с флагом
       `isControl`, и его день означает «записаться снова через N дней». Отдельной
       карточки визита с датой и кнопкой у плана нет: даты не существует, пока
       запись не создана. */

export type CareCheck = { id: string; text: string; hint: string; done: boolean };
export type CareItem = { id: string; text: string; hint: string };
/** `state` считает СЕРВЕР от часов, а не клиент от дат: «день 1» это следующий
 *  календарный день, а не «через 24 часа», и локальные сутки врача знает он. */
export type CareStageState = 'past' | 'now' | 'ahead';
export type CareStage = {
    id: string;
    day: number;
    title: string;
    isControl: boolean;
    /** сколько снимков просит врач на этом этапе; `null` — не просит */
    need: number | null;
    state: CareStageState;
    checks: CareCheck[];
};
export type CarePlan = {
    id: string;
    title: string;
    proc: string;
    /** Визит, за которым следует план, или `null` — план отправлен ИЗ КАРТЫ.
     *
     *  Кабинет соединяет им план с визитом САМ, на клиенте: сервер за визитом не
     *  ходит. Здесь он решает, КУДА кладутся снимки фотоконтроля — корнем их
     *  дерева служит визит, когда он есть, и сам план, когда его нет
     *  (`data/photos.ts`, `planRoot`). */
    appointmentId: string | null;
    /** `name` и `initials` МОГУТ быть пусты: аккаунт врача заводится из claims, а
     *  при входе по телефону имени в claims нет вовсе. Экран обязан пережить это,
     *  а не печатать пустую строку в фразе «{name}, ваш врач». */
    doctor: { name: string; initials: string; clinicName: string };
    anchorAt: string;
    note: string;
    dayNumber: number;
    dayTotal: number;
    checksDone: number;
    checksTotal: number;
    completed: boolean;
    nextDay: number | null;
    stages: CareStage[];
    can: CareItem[];
    cant: CareItem[];
    flags: CareItem[];
};

export type PlanResult =
    | { state: 'ok'; plan: CarePlan }
    /** Раздел не подключён к этому окружению (нет адреса гейтвея). */
    | { state: 'offline' }
    /** Гейтвей не пустил: узел доступа роли `patient` не выдан в дереве. */
    | { state: 'forbidden' }
    /** Плана нет, он чужой или врач его убрал — ОДИН ответ на три случая. */
    | { state: 'notfound' }
    | { state: 'error' };

export async function readPlan(channelId: string): Promise<PlanResult> {
    const base = serviceBase('post-care-api');
    if (base === null) return { state: 'offline' };
    try {
        const res = await apiFetch(
            base,
            `/read/patient/post-care/channel/plan?channelId=${encodeURIComponent(channelId)}`,
        );
        if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
        if (res.status === 404) return { state: 'notfound' };
        if (!res.ok) return { state: 'error' };
        return { state: 'ok', plan: toPlan(await res.json()) };
    } catch {
        // Сюда же попадает отказ CORS — из браузера он неотличим от обрыва сети.
        return { state: 'error' };
    }
}

/** Отметить или снять пункт. Возвращает, ПРИНЯЛ ли сервер: экран рисует отметку
 *  сразу, а на отказе откатывает — иначе галочка врала бы о том, что врач её
 *  видит. */
export async function toggleCheck(checkId: string, done: boolean): Promise<boolean> {
    const base = serviceBase('post-care-api');
    if (base === null) return false;
    try {
        const res = await apiFetch(
            base,
            `/write/patient/post-care/check?checkId=${encodeURIComponent(checkId)}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ done }),
            },
        );
        return res.ok;
    } catch {
        return false;
    }
}

/* Разбор ЯВНЫЙ, а не `body as CarePlan`. Приведение типа ничего не проверяет:
   пропущенное поле стало бы `undefined` внутри разметки, и экран напечатал бы
   «день undefined» либо упал на `.map`. Здесь же видно, какие поля кабинет
   действительно использует. */
function toPlan(raw: unknown): CarePlan {
    const r = obj(raw);
    const doc = obj(r.doctor);
    return {
        id: str(r.id),
        title: str(r.title),
        proc: str(r.proc),
        appointmentId: typeof r.appointmentId === 'string' ? r.appointmentId : null,
        doctor: {
            name: str(doc.name),
            initials: str(doc.initials),
            clinicName: str(doc.clinicName),
        },
        anchorAt: str(r.anchorAt),
        note: str(r.note),
        dayNumber: num(r.dayNumber),
        dayTotal: num(r.dayTotal),
        checksDone: num(r.checksDone),
        checksTotal: num(r.checksTotal),
        completed: r.completed === true,
        nextDay: typeof r.nextDay === 'number' ? r.nextDay : null,
        stages: list(r.stages).map(toStage),
        can: list(r.can).map(toItem),
        cant: list(r.cant).map(toItem),
        flags: list(r.flags).map(toItem),
    };
}

function toStage(raw: unknown): CareStage {
    const s = obj(raw);
    return {
        id: str(s.id),
        day: num(s.day),
        title: str(s.title),
        isControl: s.isControl === true,
        need: typeof s.need === 'number' && s.need > 0 ? s.need : null,
        state: s.state === 'past' || s.state === 'now' ? s.state : 'ahead',
        checks: list(s.checks).map(toCheck),
    };
}

const toCheck = (raw: unknown): CareCheck => {
    const c = obj(raw);
    return { id: str(c.id), text: str(c.text), hint: str(c.hint), done: c.done === true };
};
const toItem = (raw: unknown): CareItem => {
    const i = obj(raw);
    return { id: str(i.id), text: str(i.text), hint: str(i.hint) };
};

const obj = (v: unknown): Record<string, unknown> => (v ?? {}) as Record<string, unknown>;
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
